import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { SITE } from "@/lib/site";

const LINKS = [
  { id: "services", label: "Services" },
  { id: "work", label: "Work" },
  { id: "process", label: "Process" },
  { id: "about", label: "Studio" },
  { id: "contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#080A0B]/85 backdrop-blur-xl border-b border-[#27272A]/70 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="nav-brand"
          className="flex items-center gap-3 group"
        >
          <img src={SITE.logo} alt="MarX Plumbing — Concord, Sydney" className="h-16 md:h-20 w-auto object-contain" />
          <span className="eyebrow hidden sm:inline border-l border-[#27272A] pl-4 ml-1" style={{ fontSize: "0.55rem" }}>
            {SITE.suburb} · {SITE.region}
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="link-underline text-sm tracking-wide text-[#A1A1AA] hover:text-[#F4F0EB] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SITE.phoneHref}
            data-testid="nav-call-btn"
            className="hidden md:inline-flex items-center gap-2 text-[#F4F0EB] hover:text-[#3FB8DC] transition-colors text-sm"
          >
            <Phone className="w-4 h-4 text-[#3FB8DC]" />
            <span className="tracking-wider">{SITE.phone}</span>
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={() => go("contact")}
            className="hidden md:inline-flex btn-primary text-xs"
            style={{ padding: "0.7rem 1.4rem" }}
          >
            Request a Quote
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[#F4F0EB] p-2"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#080A0B] border-t border-[#27272A] px-6 py-8 flex flex-col gap-6"
        >
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-left font-serif-display text-3xl text-[#F4F0EB]"
            >
              {l.label}
            </button>
          ))}
          <a href={SITE.phoneHref} className="text-[#3FB8DC] tracking-widest text-sm mt-4">
            {SITE.phone}
          </a>
        </div>
      )}
    </header>
  );
}
